import { derived, type Readable } from 'svelte/store';
import { createQuery, type QueryClient } from '$lib/api/tanstack-query';

import { assertExists } from '$lib/assert';
import type { AuthContext } from '$lib/auth';

import type { AppServiceClient } from '../client';
import { prefetchQuery } from '../prefetch';
import {
  type BaseQueryOptions,
  createQueryOptions,
  getAuthedQueryOptions,
  useAuthedQueryOptions,
} from '../query-options';
import { locationKeys } from './query-key';

export const createGetLocationQuery = (
  locationID: Readable<string | undefined>
) => {
  const baseOptions = useAuthedQueryOptions('app');

  const options = derived(
    [baseOptions, locationID],
    ([{ enabled, getRPCOptions, client }, $locationID]) =>
      createQueryOptions({
        queryKey: locationKeys.get($locationID),
        enabled: enabled && $locationID !== undefined,
        queryFn: async () => {
          assertExists($locationID, 'Missing location ID');

          const rpcOptions = await getRPCOptions();
          const { response } = await client.getLocation(
            { locationId: $locationID },
            rpcOptions
          );

          return response.location;
        },
      })
  );

  return createQuery(options);
};

export const prefetchGetLocation = async (
  queryClient: QueryClient,
  client: AppServiceClient,
  authContext: AuthContext,
  locationID: string
) => {
  const baseAuthedOptions = getAuthedQueryOptions(client, authContext);
  const options = createGetLocationQueryOptions(baseAuthedOptions, locationID);

  return prefetchQuery(queryClient, options);
};

const createGetLocationQueryOptions = (
  { client, getRPCOptions, enabled }: BaseQueryOptions<AppServiceClient>,
  locationID: string | undefined
) =>
  createQueryOptions({
    enabled,
    queryKey: locationKeys.get(locationID),
    queryFn: async () => {
      assertExists(locationID, 'Missing location ID');

      const rpcOptions = await getRPCOptions();
      const { response } = await client.getLocation(
        { locationId: locationID },
        rpcOptions
      );

      return response.location;
    },
  });
